import {
  CascadiaSpacing,
  CascadiaTypographyStyle,
} from "@certn/cascadia-tokens";
import { formatName } from "@certn/utils";
import { css } from "@emotion/css";
import { PropsWithChildren } from "react";

import { LinkButton, Text } from "@certn/cascadia-react";

import { Avatar, AvatarProps } from "./Avatar";
import { Stack } from "../../foundations/grid-layout/Stack";
import Skeleton from "../../patterns/skeleton/Skeleton";

interface Props extends AvatarProps, PropsWithChildren {
  avatar?: React.ReactNode;
  image?: string;
  title?: string;
  subtitle?: React.ReactNode;
  textStyle?: CascadiaTypographyStyle;
  gap?: CascadiaSpacing;
  onPress?: () => void;
}

export function AvatarWithName(props: Props) {
  const {
    avatar,
    title = typeof props.name === "string"
      ? props.name
      : formatName(props.name),
    subtitle,
    textStyle = "form/body-secondary",
    gap = "space-2",
    onPress,
    size = subtitle ? "small" : "x-small",
    testID = "avatar-with-name",
    ...avatarProps
  } = props;

  if (avatarProps.isLoading) {
    return (
      <Stack orientation="horizontal" gap={gap}>
        <Avatar isLoading size={size} testID={`${testID}-avatar`} />
        <Stack gap="space-0">
          <Skeleton textStyle={textStyle} width={100} />
          {typeof props.subtitle === "string" ? (
            <Skeleton textStyle="global/info-label" width={150} />
          ) : (
            props.subtitle
          )}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack orientation="horizontal" gap={gap} className={styles.overflow}>
      {props.avatar || (
        <Avatar {...avatarProps} size={size} testID={`${testID}-avatar`} />
      )}
      <Stack gap="space-0" className={css(styles.overflow, { minWidth: 0 })}>
        {onPress ? (
          <LinkButton
            linkStyle="monochrome"
            textColor="text-base"
            textStyle={textStyle}
            testID={`${testID}-link`}
            onPress={onPress}
          >
            {title}
          </LinkButton>
        ) : (
          <Text style={textStyle} testID={`${testID}-text`} truncate>
            {title}
          </Text>
        )}
        {typeof props.subtitle === "string" ? (
          <Text style="global/info-label" color="text-weak" truncate>
            {props.subtitle}
          </Text>
        ) : (
          props.subtitle
        )}
      </Stack>
    </Stack>
  );
}

const styles = {
  overflow: css({ overFlow: "hidden" }),
};
